import { sortBy } from 'lodash';
import React, { useEffect } from 'react';

import { useAdminContext } from 'App/pages/Admin/AdminContext';
import 'App/babel/components/LegacyAdmin/sidebar';
import { useSelector, useForceRender } from 'App/components/hooks';
import i18next from 'i18next';
import history from 'App/pages/routes/history';
import useAdminRoutes from '../allAdminRoutes';
import type { match } from 'react-router-dom';

import './AdminSidebar.scss';
import { sidebar as AdminSidebarLegacy } from 'App/babel/components/LegacyAdmin/sidebar';

// Exported for reuse in angular controller
export const reactRouteMap = {
	bookedfirstappointmentgoals: '/admin/booked-first-appointment-goals',
	completedfirstappointmentgoals: '/admin/completed-first-appointment-goals',
	loginStats: '/admin/sign-in-stats',
	scripts: '/admin/scripts',
	ticketRules: '/admin/customer-support/rules',
	usersAndRoles: '/admin/usersAndRoles',
	ticketForms: '/admin/customer-support/forms',
	'2fa': '/admin/security/2fa',
	apiKeys: '/admin/security/apiKeys',
	credentials: '/admin/credentials',
	customerSupport: '/admin/customer-support/verify',
	imports: '/admin/import',
	projectplan: '/admin/projectplan',
	aiPrompts: '/admin/ai-prompts',
	anonymization: '/admin/anonymization',
	projectPlanTemplates: '/admin/project-plan/templates',
	projectPlanFields: '/admin/project-plan/fields',
	products: '/admin/products',
	brand: '/admin/brands',
	sharedAppointmentAvailability: '/admin/shared-appointment-availability',
	projectPlanWorkProcess: '/admin/project-plan/workprocess',
	projectPlanPriorityAndStatus: '/admin/project-plan/priorityandstatus',
	ticketFields: '/admin/customer-support/fields',
	fields: '/admin/fields',
	dynamicFields: '/admin/dynamic-fields',
	ticketResponseTemplates: '/admin/customer-support/responsetemplates',
	ticketAutoReplyTemplates: '/admin/customer-support/autoreplytemplates',
	creditMonitoring: '/admin/credit-monitoring',
	integrations: '/admin/integrations',
	profile: '/admin/profile',
	salesquota: '/admin/sales-quota',
	notificationCenter: '/admin/notifications',
	subscriptionIndexing: '/admin/subscriptionIndexing',
	activityTypes: '/admin/activitytypes',
	orderstages: '/admin/orderstages',
	defaultMailSignature: '/admin/email-signatures',
	optIn: '/admin/optin',
	optOut: '/admin/optOut',
	triggers: '/admin/triggers',
	documentTemplates: '/admin/documentTemplates',
	pipelinegoals: '/admin/pipeline-goals',
	phonecallgoals: '/admin/phonecall-goals',
	salesboardCard: '/admin/salesboard-card',
	salesboards: '/admin/salesboards',
	activeInvites: '/admin/activeInvites',
	listSalesboards: '/admin/listsalesboards',
	priceLists: '/admin/pricelists',
	agreement: '/admin/agreement',
	ipblock: '/admin/ip-block',
	accountsettings: '/admin/accountsettings',
	completedappointmentgoals: '/admin/completed-appointment-goals',
	bookedappointmentgoals: '/admin/booked-appointment-goals',
	appointmentAvailability: '/admin/appointment-availability',
	dynamiclinks: '/admin/dynamiclinks',
	journeyEditor: '/admin/journeyEditor',
	visitSettings: '/admin/visit',
	publicChatbot: '/admin/website-chatbot',
	reminders: '/admin/reminders',
	activityquota: '/admin/activity-quota',
	mailSignature: '/admin/mailSignature',
	accountProfile: '/admin/accountProfile',
	productquota: '/admin/product-quota',
	marketRejectlist: '/admin/marketRejectlist',
	salesprocesses: '/admin/salesprocesses',
	prospectingSignals: '/admin/prospecting-signals',
	prospectingEnrichment: '/admin/prospecting-enrichment',
	prospectingAgent: '/admin/prospecting-agent',
	userdefinedobjects: '/admin/userdefinedobjects',
	oneoffquota: '/admin/oneoff-quota',
	sharedviews: '/admin/sharedviews',
	subscriptionquota: '/admin/subscription-quota',
	salesmarginquota: '/admin/sales-margin-quota',
	opportunityRisks: '/admin/opportunity-risks',
	billing: '/admin/billing',
	mailSettings: '/admin/mailsettings',
	category: '/admin/categories',
	currencies: '/admin/currencies',
	deleteLog: '/admin/deletelog',
	looker: '/admin/looker',
	domains: '/admin/domains',
	journeyStatuses: '/admin/journeyStatuses',
	automations: '/admin/automations',
	mcpSecurity: '/admin/security/mcp'
} as const;

const AdminSidebar = ({ match }: { match: match<Record<string, string>> }) => {
	const { metadata, self } = useSelector(({ App }) => App);
	const { forced, triggerRender } = useForceRender();
	const routes = useAdminRoutes();

	const currentRoute = routes.find(route => route.path === match.path);
	const { sidebarSearch } = useAdminContext();

	useEffect(() => {
		i18next.on('languageChanged', triggerRender);
		return () => {
			i18next.off('languageChanged', triggerRender);
		};
	}, []);

	var isSelectedState = function (stateName: string, params?: any, opts?: any) {
		if (!currentRoute) {
			return false;
		}
		const currentRouteState = `administration.${currentRoute.oldRouteState}`;
		const correctRoute = stateName === currentRouteState;
		if ((opts && opts.ignoreParams) || !params) {
			return correctRoute;
		}

		let validParams = true;
		Object.keys(params).forEach(key => {
			if (params[key] !== match.params[key]) {
				validParams = false;
			}
		});
		return stateName === currentRouteState && validParams;
	};

	const setPage = (state: string, params: any) => {
		if (state in reactRouteMap) {
			const url = reactRouteMap[state as keyof typeof reactRouteMap];
			if (params?.type) {
				return history.push(`${url}/${params.type}`);
			}

			return history.push(url);
		} else if (!isSelectedState('administration.' + state, params)) {
			Tools.$state.go('administration.' + state, params);
		}
	};

	return (
		<AdminSidebarLegacy
			key={`admin-sidebar-${forced}`}
			rootData={{
				state: currentRoute?.oldRouteState ?? '',
				setPage: setPage,
				isSelectedState: isSelectedState,
				currentUrl: '',
				integrations: {
					userConfigurable: sortBy(metadata?.integrations.userConfigurable ?? [], 'name'),
					global: sortBy(metadata?.integrations.active ?? [], 'name').filter(
						integration => !integration.userOnly
					),
					mainApps: metadata?.mainApps ?? []
				},
				outsideSearch: sidebarSearch,
				self
			}}
		/>
	);
};

export default AdminSidebar;
